import React, { useState, useEffect } from 'react';
import { Box, IconButton, useMediaQuery, Fade } from '@mui/material';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import { useTheme } from '@mui/material/styles';

const IMAGE_CACHE_KEY = 'heroSlideshow';
const CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24 hours

const ImageSlideshow = ({ images, currentIndex, onLoad, onManualNavigate }) => {
    const [showControls, setShowControls] = useState(false);
    const [opacity, setOpacity] = useState(1);
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const isDark = theme.palette.mode === 'dark';

    // Modify image URL to use mobile version if on mobile
    const getResponsiveImageUrl = (url) => {
        if (!url) return url;
        return isMobile ? 
            url.replace('/desk_', '/mobile_') : 
            url.replace('/mobile_', '/desk_');
    };

    // Check cache before loading images
    useEffect(() => {
        const cached = localStorage.getItem(IMAGE_CACHE_KEY);
        if (cached) {
            const { urls, timestamp, deviceType } = JSON.parse(cached);
            const isExpired = Date.now() - timestamp > CACHE_EXPIRY;
            const isSameDevice = deviceType === (isMobile ? 'mobile' : 'desktop');
            
            if (!isExpired && urls.length === images.length && isSameDevice) {
                console.log('Using cached hero image URLs');
                return;
            }
        }

        localStorage.setItem(IMAGE_CACHE_KEY, JSON.stringify({
            urls: images,
            timestamp: Date.now(),
            deviceType: isMobile ? 'mobile' : 'desktop'
        }));
        console.log('Cached new hero image URLs');
    }, [images, isMobile]);

    // Handle fade effect
    useEffect(() => {
        setOpacity(0);
        const timer = setTimeout(() => setOpacity(1), 50);
        return () => clearTimeout(timer);
    }, [currentIndex]);

    return (
        <Box
            sx={{
                width: '100%',
                height: '100%',
                position: 'relative',
                overflow: 'hidden',
                borderRadius: { xs: '14px', sm: '20px' },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDark ? '#0A0F1D' : '#0B1120',
            }}
            onMouseEnter={() => setShowControls(true)}
            onMouseLeave={() => setShowControls(false)}
        >
            {/* Left navigation arrow */}
            <Fade in={isMobile || showControls} timeout={200}>
                <IconButton
                    aria-label="Previous slide"
                    sx={{
                        position: 'absolute',
                        left: { xs: 8, sm: 16 },
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        backgroundColor: isDark 
                            ? 'rgba(19, 28, 46, 0.75)' 
                            : 'rgba(255, 255, 255, 0.85)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
                        color: isDark ? '#FFFFFF' : '#0F172A',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
                        width: { xs: 36, sm: 44 },
                        height: { xs: 36, sm: 44 },
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            backgroundColor: isDark ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
                            transform: 'translateY(-50%) scale(1.08)',
                            boxShadow: '0 12px 28px rgba(0, 0, 0, 0.35)',
                        },
                    }}
                    onClick={() => onManualNavigate('prev')}
                >
                    <ChevronLeftRoundedIcon sx={{ fontSize: { xs: '1.4rem', sm: '1.75rem' } }} />
                </IconButton>
            </Fade>

            {/* Right navigation arrow */}
            <Fade in={isMobile || showControls} timeout={200}>
                <IconButton
                    aria-label="Next slide"
                    sx={{
                        position: 'absolute',
                        right: { xs: 8, sm: 16 },
                        top: '50%',
                        transform: 'translateY(-50%)',
                        zIndex: 2,
                        backgroundColor: isDark 
                            ? 'rgba(19, 28, 46, 0.75)' 
                            : 'rgba(255, 255, 255, 0.85)',
                        backdropFilter: 'blur(10px)',
                        WebkitBackdropFilter: 'blur(10px)',
                        border: `1px solid ${isDark ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)'}`,
                        color: isDark ? '#FFFFFF' : '#0F172A',
                        boxShadow: '0 8px 24px rgba(0, 0, 0, 0.25)',
                        width: { xs: 36, sm: 44 },
                        height: { xs: 36, sm: 44 },
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:hover': {
                            backgroundColor: isDark ? 'rgba(30, 41, 59, 0.95)' : '#FFFFFF',
                            transform: 'translateY(-50%) scale(1.08)',
                            boxShadow: '0 12px 28px rgba(0, 0, 0, 0.35)',
                        },
                    }}
                    onClick={() => onManualNavigate('next')}
                >
                    <ChevronRightRoundedIcon sx={{ fontSize: { xs: '1.4rem', sm: '1.75rem' } }} />
                </IconButton>
            </Fade>

            {/* Image display */}
            <img
                src={getResponsiveImageUrl(images[currentIndex])}
                alt={`Slideshow promo ${currentIndex + 1}`}
                style={{
                    maxWidth: '100%',
                    maxHeight: '100%',
                    objectFit: 'contain',
                    opacity: opacity,
                    transition: 'opacity 0.4s ease-in-out',
                    userSelect: 'none',
                }}
                onLoad={onLoad}
            />
        </Box>
    );
};

export default ImageSlideshow;